import {
  createContext,
  useContext,
  useEffect,
  useId,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type CSSProperties,
  type HTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
  type Ref,
} from 'react';
import { Link } from 'react-router';

/** Small primitives (style guide v2 §11). Default to less: words over icons, rules over boxes. */

/** Set by a frame that already places its pages on the shell (Settings, with its menu beside them). */
const Framed = createContext(false);

/** Pages inside this are only their column: the frame gives them the shell and the gutters. */
export function PageFrame({ children }: { children: ReactNode }) {
  return <Framed.Provider value={true}>{children}</Framed.Provider>;
}

/**
 * A page, on the shell (styles.css: the one working width, with its
 * gutters). `wide` for tables, lists, the cost bar and the drawings, which
 * use all of it (`full`, for the drawing editors, is the same); otherwise a
 * reading page, prose or a simple form, keeps its column, lined up under the
 * lockup rather than centred.
 */
export function Page({ children, wide = false, full = false }: { children: ReactNode; wide?: boolean; full?: boolean }) {
  const framed = useContext(Framed);
  const column = wide || full ? '' : 'max-w-column';
  if (framed) return <div className={`min-w-0 ${column}`}>{children}</div>;
  return <main className="shell pb-28 pt-8 sm:pt-12">{column ? <div className={column}>{children}</div> : children}</main>;
}

/** What a screen is for and how it is used: its one line, and what the question mark beside its title opens. */
export interface HowTo {
  /** One line under the title, in place of a paragraph. */
  line: string;
  /** What it is for, in a sentence. */
  intent: string;
  /** How, in three or four steps. */
  steps: string[];
  /**
   * The thing itself beside each step, as it looks on screen (the real control, or a small drawing with its classes):
   * with these, the question mark opens a sheet of steps (HowSheet) in place of the box under the title.
   */
  examples?: ReactNode[];
  /** The sheet's own title; the screen's title serves without one. */
  title?: string;
}

/**
 * A screen's title. With `how`, a circled question mark beside it opens what
 * the screen is for and how it is used, and one line stands under it in place
 * of a paragraph (design study 05, 4A; Jason, 2026-10-04: "all of this stuff is
 * too wordy. there should be a simple instructions icon that clearly explains
 * how and the intent of anything the client has to interact with").
 */
export function Title({ children, kicker, how }: { children: ReactNode; kicker?: ReactNode; how?: HowTo }) {
  const [open, setOpen] = useState(false);
  const id = useId();
  return (
    <header className="mb-6">
      {kicker ? <div className="label mb-3">{kicker}</div> : null}
      <div className="flex items-center gap-3">
        <h1 className="text-[2.125rem] sm:text-[2.5rem]">{children}</h1>
        {how ? <HowMark open={open} onToggle={() => setOpen((o) => !o)} controls={id} label="How this works" /> : null}
      </div>
      {how ? <p className="mt-1 max-w-measure text-ink-2">{how.line}</p> : null}
      {how && open ? <HowOpened id={id} how={how} title={children} onClose={() => setOpen(false)} /> : null}
    </header>
  );
}

/**
 * A section's title on a page (the home's Project process tree; design study 31): the circled question mark a page's
 * title has, with its one line under it and what it opens, at a section's size, its rule under it as the home draws one.
 */
export function SectionTitle({ children, how }: { children: ReactNode; how?: HowTo }) {
  const [open, setOpen] = useState(false);
  const id = useId();
  return (
    <div className="mb-3">
      <div className="flex items-center gap-3 border-b border-rule pb-2">
        <h2 className="text-[1.375rem] sm:text-[1.5rem]">{children}</h2>
        {how ? <HowMark open={open} onToggle={() => setOpen((o) => !o)} controls={id} label="How this works" /> : null}
      </div>
      {how ? <p className="mt-2 max-w-2xl text-ink-2">{how.line}</p> : null}
      {how && open ? <HowOpened id={id} how={how} title={children} onClose={() => setOpen(false)} /> : null}
    </div>
  );
}

/**
 * The circled question mark beside what it explains (a page's title, a section's, a phase on the funnel), named for what
 * it opens; `small` beside a phase's name on the funnel's band, its target still 44px (`hit`).
 */
export function HowMark({ open, onToggle, controls, label, small = false }: { open: boolean; onToggle: () => void; controls: string; label: string; small?: boolean }) {
  return (
    <button
      type="button"
      aria-expanded={open}
      aria-controls={controls}
      aria-label={label}
      onClick={onToggle}
      className={`hit inline-flex shrink-0 items-center justify-center rounded-full border font-display leading-none ${small ? 'h-[18px] w-[18px] text-[0.75rem]' : 'h-7 w-7 text-[1rem]'} ${open ? 'border-ink bg-ink text-paper' : 'border-ink-2 text-ink-2 hover:text-ink'}`}
    >
      ?
    </button>
  );
}

/** What the question mark opens: a sheet of steps with the thing itself beside each, or the box of what it is for and how. */
function HowOpened({ id, how, title, onClose }: { id: string; how: HowTo; title: ReactNode; onClose: () => void }) {
  if (how.examples)
    return (
      <HowSheet
        title={how.title ?? (typeof title === 'string' ? title : 'How this works')}
        line={how.intent}
        steps={how.steps.map((text, i) => ({ text, example: how.examples![i] }))}
        onClose={onClose}
      />
    );
  return <HowBox id={id} how={how} />;
}

/** The box a question mark opens: what it is for, then how, in its steps. */
export function HowBox({ id, how }: { id: string; how: HowTo }) {
  return (
    <div id={id} className="mt-3 max-w-[34rem] rounded-[2px] border border-rule-strong bg-sheet px-4 py-3 text-[0.9375rem]">
      <div className="label">What it is for</div>
      <p className="mb-2 mt-0.5">{how.intent}</p>
      <div className="label">How</div>
      <ol className="mt-1 list-decimal pl-5">
        {how.steps.map((s) => (
          <li key={s} className="mb-0.5">
            {s}
          </li>
        ))}
      </ol>
    </div>
  );
}

/**
 * How something works, as a sheet of steps (design study 05, 4A; How to
 * report, Jason, 2026-10-06: "show actual examples in the images"): a title,
 * one line, then the steps two across on a wide screen, each the thing itself
 * as it looks on screen and its line. Esc or a click outside closes it. It sits
 * at the top and leaves the foot of the window to the feedback button.
 */
export function HowSheet({ title, line, steps, onClose, children }: { title: string; line?: string; steps: { text: ReactNode; example: ReactNode }[]; onClose: () => void; children?: ReactNode }) {
  const id = useId();
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div data-feedback-ui className="fixed inset-0 z-[55] flex items-start justify-center bg-ink/25 p-2 sm:p-6" onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={id}
        className="max-h-[calc(100dvh-9.5rem)] w-full max-w-3xl overflow-y-auto rounded-[8px] bg-sheet px-5 pb-6 pt-5 shadow-[0_24px_70px_rgba(20,22,19,.18)] sm:max-h-[calc(100dvh-10.5rem)] sm:px-7 sm:pb-7"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-1 flex items-start justify-between gap-4">
          <h2 id={id} className="text-[1.5rem] leading-tight sm:text-[1.75rem]">
            {title}
          </h2>
          <Button tone="quiet" onClick={onClose}>
            Close
          </Button>
        </div>
        {line ? <p className="mb-5 text-[0.9375rem] text-ink-2">{line}</p> : null}
        <ol className="grid gap-x-8 gap-y-6 sm:grid-cols-2">
          {steps.map((s, i) => (
            <HowStep key={i} n={i + 1} example={s.example}>
              {s.text}
            </HowStep>
          ))}
        </ol>
        {children}
      </div>
    </div>
  );
}

/** One step: the thing as it looks on screen, then its number and its line. */
export function HowStep({ n, example, children }: { n: number; example: ReactNode; children: ReactNode }) {
  return (
    <li>
      <div className="flex h-36 items-center justify-center overflow-hidden rounded-[4px] border border-rule bg-paper px-4" aria-hidden="true">
        {example}
      </div>
      <p className="mt-2.5 grid grid-cols-[1.25rem_1fr] text-[0.9375rem] leading-relaxed">
        <span className="label pt-[0.3em]">{n}</span>
        <span>{children}</span>
      </p>
    </li>
  );
}

/** A word the reader will find on screen, set apart in a line of help. */
export const HowWord = ({ children }: { children: ReactNode }) => <span className="font-medium text-ink">{children}</span>;

/** One line back to where this page belongs; nothing more. */
export function BackLink({ to, label }: { to: string; label: string }) {
  return (
    <div className="mb-2">
      <Link to={to} className="label hover:!text-ink">
        ← {label}
      </Link>
    </div>
  );
}

export function Section({ title, children, aside }: { title: string; children: ReactNode; aside?: ReactNode }) {
  return (
    <section className="mt-12">
      <div className="mb-3 flex items-baseline justify-between gap-4 border-b border-rule pb-2">
        <h2 className="text-[1.375rem] sm:text-[1.5rem]">{title}</h2>
        {aside}
      </div>
      {children}
    </section>
  );
}

type Tone = 'primary' | 'secondary' | 'quiet' | 'danger';

export function Button({
  tone = 'primary',
  busy = false,
  disabled,
  className = '',
  children,
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & { tone?: Tone; busy?: boolean; ref?: Ref<HTMLButtonElement> }) {
  const base =
    'inline-flex items-center justify-center gap-2 transition-colors duration-[140ms] ease-drafting disabled:cursor-not-allowed disabled:opacity-40';
  const tones: Record<Tone, string> = {
    primary: 'min-h-[42px] rounded-[3px] bg-ink px-5 text-paper hover:bg-[#363b35]',
    secondary: 'min-h-[42px] rounded-[3px] border border-rule-strong px-4 text-ink hover:border-ink',
    quiet: 'link min-h-[44px] px-0 text-ink-2 hover:text-ink',
    danger: 'link min-h-[44px] px-0 text-alert',
  };
  return (
    // Busy is disabled whatever else is said: a second click while it works never sends it twice.
    <button type="button" className={`${base} ${tones[tone]} ${className}`} {...rest} disabled={busy || disabled}>
      {busy ? 'Working…' : children}
    </button>
  );
}

export function TextLink({ to, children, className = '' }: { to: string; children: ReactNode; className?: string }) {
  return (
    <Link to={to} className={`link ${className}`}>
      {children}
    </Link>
  );
}

export function Field({ label, hint, children }: { label: string; hint?: ReactNode; children: (id: string) => ReactNode }) {
  const id = useId();
  return (
    <div className="mb-5">
      <label htmlFor={id} className="label block">
        {label}
      </label>
      {children(id)}
      {hint ? <p className="meta mt-1">{hint}</p> : null}
    </div>
  );
}

export function TextInput(props: InputHTMLAttributes<HTMLInputElement>) {
  return <input className="field" {...props} />;
}

/** A plain number; blank stays blank (null). */
export function NumberInput({
  value,
  onChange,
  id,
  placeholder = '—',
  className = '',
  disabled = false,
  'aria-label': ariaLabel,
}: {
  value: number | null;
  onChange: (n: number | null) => void;
  id?: string;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  'aria-label'?: string;
}) {
  const [text, setText] = useState(value === null ? '' : String(value));
  const [focused, setFocused] = useState(false);
  const shown = focused ? text : value === null ? '' : String(value);
  return (
    <input
      id={id}
      aria-label={ariaLabel}
      inputMode="decimal"
      className={`field num ${className}`}
      value={shown}
      disabled={disabled}
      placeholder={placeholder}
      onFocus={() => {
        setText(value === null ? '' : String(value));
        setFocused(true);
      }}
      onChange={(e) => setText(e.target.value)}
      onBlur={() => {
        setFocused(false);
        const t = text.replace(/,/g, '').trim();
        const n = t === '' ? null : Number(t);
        if (n === null || Number.isFinite(n)) {
          if (n !== value) onChange(n);
        } else {
          setText(value === null ? '' : String(value));
        }
      }}
    />
  );
}

/**
 * One of a few, side by side (the kinds, who does a step, who approves a gate): the chosen one dark. A second click on
 * the one chosen leaves it blank again, unless `keep` holds a choice once made.
 */
export function Seg<T extends string>({
  value,
  options,
  words,
  onPick,
  label,
  marks,
  names,
  keep = false,
}: {
  value: T | null;
  options: readonly T[];
  words: (o: T) => string;
  onPick: (o: T | null) => void;
  label: string;
  /** A mark before each one's words (the kinds); one with no words is its mark alone, read by its name. */
  marks?: (o: T) => ReactNode;
  names?: (o: T) => string;
  /** A choice once made stays made: clicking the one chosen keeps it. */
  keep?: boolean;
}) {
  return (
    <div className="inline-flex flex-wrap overflow-hidden rounded-[2px] border border-rule-strong" role="radiogroup" aria-label={label}>
      {options.map((o, i) => (
        <button
          key={o}
          type="button"
          role="radio"
          aria-checked={value === o}
          aria-label={words(o) ? undefined : names?.(o)}
          title={words(o) ? undefined : names?.(o)}
          // A second click on the one chosen leaves it blank again.
          onClick={() => onPick(value === o && !keep ? null : o)}
          className={`inline-flex min-h-9 items-center gap-1.5 px-2.5 text-[0.8125rem] ${i ? 'border-l border-rule-strong' : ''} ${value === o ? 'bg-ink text-paper' : 'text-ink-2 hover:bg-inset hover:text-ink'}`}
        >
          {marks?.(o)}
          {words(o)}
        </button>
      ))}
    </div>
  );
}

/**
 * One of a few, as rows under a named set: each a radio with its words and a line under them (what choosing it
 * makes), the whole row its 44px target. For a choice that needs its line read before it is made; `Seg` is the
 * compact one.
 */
export function Choices<T extends string>({
  legend,
  value,
  options,
  onPick,
  disabled = false,
}: {
  legend: string;
  value: T;
  options: readonly { value: T; label: ReactNode; hint?: ReactNode }[];
  onPick: (v: T) => void;
  disabled?: boolean;
}) {
  const name = useId();
  return (
    <fieldset className="mb-5">
      <legend className="label mb-1 block">{legend}</legend>
      <div className="border-y border-rule">
        {options.map((o) => (
          <label key={o.value} className="flex min-h-11 cursor-pointer items-baseline gap-3 border-b border-rule py-3 last:border-b-0">
            <input
              type="radio"
              name={name}
              className="relative top-0.5 size-4 shrink-0 accent-moss"
              checked={value === o.value}
              disabled={disabled}
              onChange={() => onPick(o.value)}
            />
            <span>
              <span className="block">{o.label}</span>
              {o.hint ? <span className="block text-sm text-ink-3">{o.hint}</span> : null}
            </span>
          </label>
        ))}
      </div>
    </fieldset>
  );
}

/**
 * A switch: the one control that stays pill-shaped, because the geometry is
 * the state. The whole row is the control (its words name it, its hint
 * describes it), so the words are as clickable as the switch.
 */
export function Toggle({ checked, onChange, label, hint }: { checked: boolean; onChange: (v: boolean) => void; label: string; hint?: string }) {
  const id = useId();
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-labelledby={`${id}-label`}
      aria-describedby={hint ? `${id}-hint` : undefined}
      onClick={() => onChange(!checked)}
      className="flex min-h-11 cursor-pointer items-start gap-3 py-2.5 text-left"
    >
      <span
        aria-hidden="true"
        className={`mt-0.5 inline-flex h-5 w-9 shrink-0 items-center rounded-full p-0.5 transition-colors duration-[140ms] ease-drafting motion-reduce:transition-none ${checked ? 'bg-moss' : 'bg-rule-strong'}`}
      >
        <span className={`h-4 w-4 rounded-full bg-sheet transition-transform duration-[140ms] ease-drafting motion-reduce:transition-none ${checked ? 'translate-x-4' : ''}`} />
      </span>
      <span>
        <span id={`${id}-label`} className="block">
          {label}
        </span>
        {hint ? (
          <span id={`${id}-hint`} className="meta block">
            {hint}
          </span>
        ) : null}
      </span>
    </button>
  );
}

/**
 * How complete something is: a thin bar, moss over stone, as the tree builder
 * draws its nodes and its whole tree and a project's home draws its details
 * (Jason, 2026-10-05: "use the same completeness pattern").
 */
export function CompletenessBar({ done, of, label, className = 'w-48' }: { done: number; of: number; label?: string; className?: string }) {
  return (
    <span className={`inline-block h-[6px] shrink-0 bg-stone ${className}`} role="img" aria-label={label ?? `${done} of ${of}`}>
      <span className="block h-full bg-moss" style={{ width: `${of ? (100 * done) / of : 0}%` }} />
    </span>
  );
}

export function ErrorLine({ error }: { error: string | null | undefined }) {
  if (!error) return null;
  return (
    <p role="alert" className="mt-3 text-sm text-alert">
      {error}
    </p>
  );
}

export function Note({ children }: { children: ReactNode }) {
  return <p className="meta mt-2">{children}</p>;
}

/**
 * Where the notice sits: the window's foot by its right corner, above the 44px button kept there, clear of the insets;
 * and above the bar of changes not saved while it is up (`UnsavedBar` sets `--foot-bar` to its height), never under it.
 */
const NOTICE_SPOT: CSSProperties = {
  right: 'calc(1rem + env(safe-area-inset-right))',
  bottom: 'max(calc(4.25rem + env(safe-area-inset-bottom)), calc(var(--foot-bar, 0px) + 0.5rem))',
};

/**
 * A word that something just happened ("Saved"): a drawing label in DM Mono, ink inside a hair rule of moss on a sheet
 * washed with it (the made colour, as a draft not saved yet is ringed in cedar), a tick drawn in moss before it, the word
 * carrying the meaning. It sits at the window's foot by its right corner, above the
 * button that lives there, on every page and width, and never takes a click. One at a time: new words replace the old,
 * and `shown` false fades it out (at once where motion is reduced). The live region stays in the page, empty between
 * notices, so each new word is read once, politely.
 */
export function Notice({ words, shown, ...rest }: { words: string | null; shown: boolean } & Omit<HTMLAttributes<HTMLDivElement>, 'children'>) {
  return (
    <div {...rest} role="status" aria-live="polite" aria-atomic="true" className="pointer-events-none fixed z-[80] flex justify-end" style={NOTICE_SPOT}>
      {words ? (
        <span
          className={`label inline-flex min-h-10 animate-notice-in items-center gap-2 rounded-[3px] border border-moss bg-[color-mix(in_srgb,var(--color-moss)_12%,var(--color-sheet))] px-3.5 !text-[0.8125rem] !text-ink shadow-[0_6px_18px_rgba(20,22,19,.08)] transition-[opacity,translate] duration-[240ms] ease-drafting motion-reduce:animate-none motion-reduce:transition-none ${shown ? 'opacity-100' : 'translate-y-1 opacity-0'}`}
        >
          <svg aria-hidden="true" width="11" height="9" viewBox="0 0 11 9" className="shrink-0">
            <path d="M1 4.6 4 7.6 10 1.2" fill="none" stroke="var(--color-moss-deep)" strokeWidth="1.6" strokeLinecap="square" />
          </svg>
          {words}
        </span>
      ) : null}
    </div>
  );
}

/**
 * Changes not saved (P-29; design study 28, A): while a form has changes it has not saved, this bar holds to the
 * window's foot, on the sheet under a rule of ink: "Changes not saved · Jurisdiction" (the fields by their labels), then
 * Discard, quiet, and Save, the primary, which does what the form's own Save does. Its row lines up on the shell and
 * stops short of the button kept at the bottom right; on a phone the words take the first line and the two buttons the
 * second, 16px from each edge. Its height is `--foot-bar` on the page while it is up, so the Saved notice rises clear of
 * it. The form's refusal reads here too, where its Save was pressed. Drawn once, by the portal's mount of it.
 */
export function UnsavedBar({
  names,
  onSave,
  onDiscard,
  busy = false,
  canSave = true,
  saveWords = 'Save',
  lead = 'Changes not saved',
  error = null,
}: {
  names: string;
  onSave: () => void;
  onDiscard: () => void;
  busy?: boolean;
  canSave?: boolean;
  saveWords?: string;
  lead?: string;
  error?: string | null;
}) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    const root = document.documentElement;
    if (!el) return;
    const measure = () => root.style.setProperty('--foot-bar', `${el.offsetHeight}px`);
    measure();
    const watch = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
    watch?.observe(el);
    return () => {
      watch?.disconnect();
      root.style.removeProperty('--foot-bar');
    };
  }, []);
  const words = error ? `Not saved: ${error}` : `${lead}${names ? ` · ${names}` : ''}`;
  // On a phone a long Save ("Save for this project") is Save, so the two buttons stay clear of the one kept beside them.
  const short = saveWords.startsWith('Save') ? 'Save' : saveWords;
  return (
    <div ref={ref} role="region" aria-label={lead} className="fixed inset-x-0 bottom-0 z-50 border-t border-ink bg-sheet" style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}>
      <div className="foot-bar-row flex flex-col gap-2 pb-3 pt-2.5 sm:flex-row sm:items-center sm:justify-between sm:gap-6 sm:py-2.5">
        <p className="min-w-0 truncate" title={words}>
          {error ? (
            <span className="text-alert">{words}</span>
          ) : (
            <>
              <span className="font-medium">{lead}</span>
              {names ? <span className="text-ink-2"> · {names}</span> : null}
            </>
          )}
        </p>
        <div className="flex shrink-0 items-center gap-5 sm:gap-6">
          <Button tone="quiet" onClick={onDiscard}>
            Discard
          </Button>
          <Button className="!min-h-11" busy={busy} disabled={!canSave} onClick={onSave}>
            {short === saveWords ? (
              saveWords
            ) : (
              <>
                <span className="sm:hidden">{short}</span>
                <span className="hidden sm:inline">{saveWords}</span>
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}

/**
 * Asked before leaving a page with changes not saved (P-29): what is not saved, then Stay on this page, the first
 * answer and the one Esc gives, and Leave without saving. Above everything but the Saved notice.
 */
export function LeaveQuestion({ names, onStay, onLeave }: { names: string; onStay: () => void; onLeave: () => void }) {
  const id = useId();
  const stay = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    stay.current?.focus();
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onStay();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onStay]);
  return (
    <div className="fixed inset-0 z-[75] flex items-center justify-center bg-ink/25 p-4" onClick={onStay}>
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={`${id}-title`}
        aria-describedby={`${id}-line`}
        className="w-full max-w-md rounded-[4px] border border-rule-strong bg-sheet px-5 pb-5 pt-4 shadow-[0_24px_70px_rgba(20,22,19,.18)]"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id={`${id}-title`} className="text-[1.5rem] leading-tight">
          Leave without saving?
        </h2>
        <p id={`${id}-line`} className="mt-2 text-ink-2">
          Changes not saved{names ? `: ${names}` : ''}. Leaving now loses them.
        </p>
        <div className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-2">
          <Button ref={stay} className="!min-h-11" onClick={onStay}>
            Stay on this page
          </Button>
          <Button tone="danger" onClick={onLeave}>
            Leave without saving
          </Button>
        </div>
      </div>
    </div>
  );
}

export function Loading() {
  return <p className="label animate-pulse py-10">Loading</p>;
}

export function LoadError({ error }: { error: unknown }) {
  const msg = error instanceof Error ? error.message : 'Could not load this page.';
  return (
    <Page>
      <p className="py-10 text-ink-2">{msg}</p>
    </Page>
  );
}

/** A drawing label: mono, thin rule, 2px corners. */
export function Tag({ children, tone = 'plain' }: { children: ReactNode; tone?: 'plain' | 'moss' | 'slate' | 'alert' | 'cedar' }) {
  const tones = {
    plain: 'border-rule-strong text-ink-2',
    moss: 'border-moss/50 text-moss-deep',
    slate: 'border-slate/50 text-slate',
    alert: 'border-alert/50 text-alert',
    cedar: 'border-cedar/60 text-cedar',
  };
  return <span className={`label inline-block rounded-[2px] border px-1.5 py-px !text-[0.65rem] leading-[1.35] ${tones[tone]}`}>{children}</span>;
}

export function Rows({ children }: { children: ReactNode }) {
  return <ul className="divide-y divide-rule border-y border-rule">{children}</ul>;
}

/** Asked once, inline: the prompt, then its two answers (Yes and No, or the caller's own words for what each does). */
export function Confirm({
  prompt,
  onYes,
  onNo,
  busy,
  yes = 'Yes',
  no = 'No',
}: {
  prompt: string;
  onYes: () => void;
  onNo: () => void;
  busy?: boolean;
  yes?: string;
  no?: string;
}) {
  return (
    <div className="mt-3 flex flex-wrap items-center gap-4 text-sm">
      <span>{prompt}</span>
      <Button tone="danger" onClick={onYes} busy={busy}>
        {yes}
      </Button>
      <Button tone="quiet" onClick={onNo}>
        {no}
      </Button>
    </div>
  );
}
